import { useState, type ReactNode } from 'react';
import { ArrowRight, Info, MapPin } from 'lucide-react';
import { EVENT_TYPE_LABEL, eventLabel } from '@wulftime/core';
import { fmtDateTime, fmtMinutes, fmtTime, fmtTimeSec } from '../lib/format';
import { Badge, Card, DataTable, EmptyState, Tabs, cx } from './ui';
import { AnomalyBadge, DayTypeBadge, GpsBadge, IncidentStatusBadge, RecordStatusBadge, RequestStatusBadge } from './status';
import { ClockMap } from './map';

export interface RecordDetailData {
  record: any;
  employee: { id: string; code: string; firstName: string; lastName: string } | null;
  policyVersion: { id: string; version: number; validFrom: string } | null;
  events: any[];
  incidents: any[];
  corrections: any[];
  audit: any[];
}

function Col({ title, tone, children }: { title: string; tone: string; children: ReactNode }) {
  return (
    <div className="card overflow-hidden">
      <div className={cx('px-4 py-2 text-xs font-semibold uppercase tracking-wide', tone)}>{title}</div>
      <dl className="space-y-2 p-4 text-sm">{children}</dl>
    </div>
  );
}
const Row = ({ k, v, strong }: { k: string; v: ReactNode; strong?: boolean }) => (
  <div className="flex items-center justify-between gap-3">
    <dt className="text-slate-500">{k}</dt>
    <dd className={cx('tabular-nums', strong ? 'font-semibold text-slate-900' : 'text-slate-800')}>{v}</dd>
  </div>
);

/** Vista individual de una jornada: PLANIFICADO · REAL · COMPUTABLE · incidencias · GPS · auditoría. */
export function RecordDetailView({ data, showAudit, actions }: { data: RecordDetailData; showAudit?: boolean; actions?: (event: any) => ReactNode }) {
  const [tab, setTab] = useState<'events' | 'gps' | 'incidents' | 'audit' | 'explain'>('events');
  const r = data.record;
  const tz = r.timezone;
  const c = r.computed;
  const p = r.planned;
  const gpsPoints = data.events
    .filter((e) => e.gps?.lat != null)
    .map((e) => ({
      id: e.id,
      lat: e.gps.lat,
      lng: e.gps.lng,
      accuracy: e.gps.accuracyMeters,
      color: e.gps.result === 'VALID' ? '#059669' : e.gps.result === 'OUTSIDE_ZONE' ? '#dc2626' : '#d97706',
      title: `${e.typeLabel} · ${e.localTime}`,
      lines: [e.gps.resultLabel, e.gps.distanceMeters != null ? `${e.gps.distanceMeters} m del centro (radio ${e.gps.radiusMeters} m)` : '', e.gps.accuracyMeters != null ? `Precisión ±${e.gps.accuracyMeters} m` : ''].filter(Boolean),
    }));
  const centers = [...new Map(data.events.filter((e) => e.gps?.center).map((e) => [e.gps.center.id, e.gps.center])).values()];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <RecordStatusBadge status={r.status} />
        <DayTypeBadge dayType={r.dayType} label={p.label} />
        {r.closed ? <Badge color="slate">Jornada cerrada</Badge> : <Badge color="blue">Jornada abierta</Badge>}
        {data.policyVersion && <Badge color="violet">Política v{data.policyVersion.version}</Badge>}
        {r.anomalyCodes?.map((a: string) => <AnomalyBadge key={a} code={a} />)}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Col title="Planificado" tone="bg-slate-100 text-slate-600">
          <Row k="Jornada" v={p.segments?.length ? p.segments.map((s: any) => `${s.startLocal}–${s.endLocal}`).join(' / ') : p.flexible ? 'Flexible' : '—'} />
          {(p.breaks ?? []).map((b: any) => (
            <Row key={b.id} k={`Pausa: ${b.name}`} v={b.startAt ? `${fmtTime(b.startAt, tz)}–${fmtTime(b.endAt, tz)}` : `${b.durationMinutes} min`} />
          ))}
          <Row k="Horas previstas" v={fmtMinutes(c?.planned.minutes ?? r.plannedMinutes)} strong />
          <Row k="Origen" v={{ OVERRIDE: 'Cambio puntual', SHIFT: 'Turno', SCHEDULE: 'Horario', NONE: '—' }[p.source?.type as string] ?? '—'} />
        </Col>
        <Col title="Real" tone="bg-emerald-50 text-emerald-800">
          <Row k="Entrada real" v={fmtTime(c?.real.firstIn, tz)} />
          {(c?.real.breaks ?? []).map((b: any, i: number) => (
            <Row key={i} k={`Pausa${b.breakName ? `: ${b.breakName}` : ''}`} v={`${fmtTime(b.start, tz)}–${b.end ? fmtTime(b.end, tz) : '…'} · ${b.minutes} min`} />
          ))}
          <Row k="Salida real" v={c?.real.lastOut ? fmtTime(c.real.lastOut, tz) : c?.open ? 'En curso' : '—'} />
          <Row k="Tiempo efectivo real" v={fmtMinutes(c?.real.effectiveMinutes)} strong />
        </Col>
        <Col title="Computable" tone="bg-brand-50 text-brand-800">
          <Row k="Entrada computable" v={fmtTime(c?.computable.firstIn, tz)} />
          <Row k="Salida computable" v={fmtTime(c?.computable.lastOut, tz)} />
          <Row k="Pausa no computable" v={`${c?.computable.breakMinutesDeducted ?? 0} min`} />
          {c?.computable.autoDeductedMinutes ? <Row k="Descuento automático" v={`${c.computable.autoDeductedMinutes} min`} /> : null}
          <Row k="Tiempo efectivo computable" v={fmtMinutes(c?.computable.effectiveMinutes)} strong />
          <Row k="Diferencia vs. previsto" v={fmtMinutes(c?.differences.vsPlannedMinutes, { sign: true })} />
          {c?.differences.excessMinutes > 0 && <Row k="Exceso respecto a jornada prevista" v={fmtMinutes(c.differences.excessMinutes)} />}
        </Col>
      </div>

      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'events', label: 'Eventos', count: data.events.length },
          { id: 'gps', label: 'GPS', count: gpsPoints.length },
          { id: 'incidents', label: 'Incidencias y correcciones', count: data.incidents.length + data.corrections.length },
          { id: 'explain', label: 'Cómo se ha computado' },
          ...(showAudit ? [{ id: 'audit' as const, label: 'Auditoría', count: data.audit.length }] : []),
        ]}
      />

      {tab === 'events' && (
        <Card padded={false}>
          <DataTable
            rowKey={(e) => e.id}
            rows={data.events}
            empty={<EmptyState title="Sin fichajes" description="No hay eventos registrados en esta jornada." />}
            columns={[
              {
                key: 't',
                header: 'Evento',
                cell: (e) => (
                  <div className={cx(e.status !== 'ACTIVE' && 'opacity-50')}>
                    <p className={cx('font-medium', e.status !== 'ACTIVE' && 'line-through')}>{eventLabel(e)}</p>
                    {e.status === 'SUPERSEDED' && <p className="text-xs text-slate-500">Sustituido por corrección</p>}
                    {e.status === 'VOIDED' && <p className="text-xs text-slate-500">Anulado por corrección</p>}
                  </div>
                ),
              },
              { key: 'h', header: 'Hora', cell: (e) => <span className="tabular-nums">{fmtTimeSec(e.occurredAt, tz)}</span> },
              {
                key: 'o',
                header: 'Origen',
                cell: (e) => (
                  <div className="text-xs">
                    <p>{e.sourceLabel}</p>
                    <p className="text-slate-500">{e.personal ? 'Fichaje personal' : 'No personal'}{e.actor ? ` · ${e.actor}` : ''}</p>
                  </div>
                ),
              },
              {
                key: 'b',
                header: 'Base temporal',
                cell: (e) => (
                  <span className="text-xs">
                    {e.timestampBasis === 'SERVER' ? 'Hora del servidor' : e.timestampBasis === 'DEVICE' ? <Badge color="amber">Dispositivo (offline)</Badge> : 'Hora aprobada en corrección'}
                    {e.needsReview && <Badge color="amber" className="ml-1">Revisar</Badge>}
                  </span>
                ),
              },
              { key: 'g', header: 'GPS', cell: (e) => (e.gps ? <GpsBadge result={e.gps.result} distance={e.gps.distanceMeters} /> : <span className="text-xs text-slate-400">—</span>) },
              { key: 'r', header: 'Motivo / anomalías', cell: (e) => <div className="max-w-xs space-y-1 text-xs">{e.reason && <p className="text-slate-700">“{e.reason}”</p>}{e.anomalies?.map((a: any) => <AnomalyBadge key={a.code} code={a.code} />)}</div> },
              ...(actions ? [{ key: 'a', header: '', cell: (e: any) => actions(e) }] : []),
            ]}
          />
        </Card>
      )}

      {tab === 'gps' &&
        (gpsPoints.length ? (
          <div className="space-y-3">
            <ClockMap points={gpsPoints} centers={centers} />
            <p className="flex items-center gap-1.5 text-xs text-slate-500">
              <MapPin className="h-3.5 w-3.5" /> Ubicaciones registradas únicamente en el momento de cada fichaje. El círculo discontinuo es el radio permitido del centro.
            </p>
          </div>
        ) : (
          <Card>
            <EmptyState icon={<MapPin className="h-5 w-5" />} title="Sin ubicaciones" description="La política aplicable no exige GPS o no se ha compartido ubicación en esta jornada." />
          </Card>
        ))}

      {tab === 'incidents' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card title="Incidencias" padded={false}>
            {data.incidents.length ? (
              <ul className="divide-y divide-slate-100">
                {data.incidents.map((i) => (
                  <li key={i.id} className="flex items-start justify-between gap-3 px-5 py-3 text-sm">
                    <div>
                      <p className="font-medium">{i.title}</p>
                      <p className="text-xs text-slate-500 whitespace-pre-line">{i.description}</p>
                    </div>
                    <IncidentStatusBadge status={i.status} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="Sin incidencias" />
            )}
          </Card>
          <Card title="Correcciones" padded={false}>
            {data.corrections.length ? (
              <ul className="divide-y divide-slate-100">
                {data.corrections.map((x) => (
                  <li key={x.id} className="px-5 py-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">{{ ADD_EVENT: 'Añadir fichaje', CHANGE_TIME: 'Cambiar hora', VOID_EVENT: 'Anular fichaje' }[x.kind as string]}{x.eventType ? ` · ${EVENT_TYPE_LABEL[x.eventType as keyof typeof EVENT_TYPE_LABEL]}` : ''}</p>
                      <RequestStatusBadge status={x.status} />
                    </div>
                    <p className="mt-1 flex items-center gap-1 text-xs text-slate-600">
                      {x.previousAt && <>{fmtTime(x.previousAt, tz)} <ArrowRight className="h-3 w-3" /></>} {x.proposedAt ? fmtTime(x.proposedAt, tz) : x.kind === 'VOID_EVENT' ? 'anular' : ''}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">“{x.reason}”</p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="Sin correcciones" />
            )}
          </Card>
        </div>
      )}

      {tab === 'explain' && (
        <Card>
          <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
            <Info className="h-4 w-4" /> Cómputo calculado con la política{data.policyVersion ? ` versión ${data.policyVersion.version} (vigente desde ${data.policyVersion.validFrom})` : ' por defecto'} y la planificación de ese día. La hora real nunca se modifica.
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            {(c?.explanation ?? []).map((l: string, i: number) => (
              <li key={i}>{l}</li>
            ))}
            {c?.inconsistencies?.map((l: string, i: number) => (
              <li key={`i${i}`} className="text-red-700">
                {l}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {tab === 'audit' && showAudit && (
        <Card padded={false}>
          <DataTable
            rowKey={(a) => a.id}
            rows={data.audit}
            columns={[
              { key: 's', header: '#', cell: (a) => <span className="text-xs text-slate-400">{a.seq}</span> },
              { key: 'd', header: 'Fecha', cell: (a) => <span className="whitespace-nowrap text-xs">{fmtDateTime(a.createdAt, tz)}</span> },
              { key: 'a', header: 'Acción', cell: (a) => <code className="text-xs">{a.action}</code> },
              { key: 'u', header: 'Actor', cell: (a) => <span className="text-xs">{a.actorLabel ?? a.actorType}</span> },
              { key: 'r', header: 'Motivo', cell: (a) => <span className="text-xs text-slate-600">{a.reason ?? '—'}</span> },
              { key: 'h', header: 'Hash', cell: (a) => <code className="text-[10px] text-slate-400">{a.hash.slice(0, 12)}…</code> },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
